import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/lib/auth";

/**
 * Finalises the Emergent Google Auth round-trip.
 * The user lands here with `#session_id=<id>` in the URL fragment.
 * We POST it to the backend, backend sets the httpOnly `session_token`
 * cookie, then we redirect to the dashboard with user state.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError("Missing session id. Please try signing in again.");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        await axios.post(
          `${API}/auth/google/callback`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        // Clear the fragment so a refresh doesn't re-run the exchange
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        await refresh();
        navigate("/affiliate/dashboard", { replace: true });
      } catch (err) {
        const msg = err?.response?.data?.detail || "Could not complete sign-in. Please try again.";
        setError(msg);
      }
    })();
  }, [location, navigate, refresh]);

  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)] flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        {error ? (
          <>
            <h1 className="font-display text-3xl mb-4">Sign-in failed</h1>
            <p className="text-[var(--muted)] mb-6">{error}</p>
            <a
              href="/affiliate/signup"
              className="inline-block px-6 py-3 rounded-full bg-[var(--marigold)] text-white font-medium"
              data-testid="auth-callback-retry"
            >
              Try again
            </a>
          </>
        ) : (
          <>
            <div className="mx-auto w-12 h-12 rounded-full border-4 border-[var(--marigold)] border-t-transparent animate-spin mb-6" />
            <p className="text-[var(--muted)] font-mono-caption tracking-[0.2em] uppercase text-xs">
              Finishing sign-in…
            </p>
          </>
        )}
      </div>
    </div>
  );
}
